import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { CheckIcon, ChevronRightIcon, SlidersHorizontalIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { trackSearchFacilityFilterApplied } from '@/lib/track'
import {
  FACILITY_GROUPS,
  formatFacilitySummary,
  parseFacilityKeys,
  serializeFacilityKeys,
} from '../utils/facilityOptions'
import { useSearchFilters } from './useSearchFilters'

const FILTER_HINT = 'Zobrazíme jen ubytování, která mají všechno vybrané vybavení.'

function useFacilitySelection() {
  const { localFilters, filters, updateLocalFilters } = useSearchFilters()
  const selectedKeys = parseFacilityKeys(localFilters.facility)

  const toggle = (key: string) => {
    const next = selectedKeys.includes(key)
      ? selectedKeys.filter((selected) => selected !== key)
      : [...selectedKeys, key]
    updateLocalFilters({ facility: serializeFacilityKeys(next) })
  }

  const clear = () => {
    updateLocalFilters({ facility: undefined })
  }

  const trackIfChanged = () => {
    if (serializeFacilityKeys(selectedKeys) !== filters.facility) {
      trackSearchFacilityFilterApplied(selectedKeys)
    }
  }

  return { selectedKeys, toggle, clear, trackIfChanged }
}

function FacilityChecklist({ selectedKeys, onToggle }: { selectedKeys: string[]; onToggle: (key: string) => void }) {
  return (
    <div className="flex flex-col gap-5">
      {FACILITY_GROUPS.map((group) => (
        <div key={group.label} className="flex flex-col gap-2">
          <p className="text-xs font-semibold tracking-wider text-slate-400 uppercase">{group.label}</p>
          <div className="flex flex-wrap gap-2">
            {group.options.map((option) => {
              const selected = selectedKeys.includes(option.key)
              return (
                <button
                  key={option.key}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => onToggle(option.key)}
                  className={cn(
                    'flex cursor-pointer items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm transition-colors outline-none focus-visible:ring-2 focus-visible:ring-slate-400',
                    selected
                      ? 'border-slate-900 bg-slate-900 text-white'
                      : 'border-slate-200 bg-white text-slate-700 hover:border-slate-400',
                  )}
                >
                  {selected && <CheckIcon className="size-3.5" />}
                  {option.label}
                </button>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

export function FacilityFilterDesktop() {
  const { commitFilters } = useSearchFilters()
  const { selectedKeys, toggle, clear, trackIfChanged } = useFacilitySelection()
  const [open, setOpen] = useState(false)

  const handleClose = () => {
    trackIfChanged()
    commitFilters()
    setOpen(false)
  }

  return (
    <div className="flex grow basis-0 flex-col gap-1">
      <span className="text-sm text-slate-400">Vybavení</span>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            className="justify-start border-0 bg-slate-100 text-left text-sm! font-normal focus-visible:bg-slate-100 focus-visible:ring-1 focus-visible:ring-slate-200"
          >
            <SlidersHorizontalIcon className="text-red h-4 w-4" />
            <span className="truncate">{formatFacilitySummary(selectedKeys)}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[min(560px,calc(100vw-2rem))]" align="end" onInteractOutside={handleClose}>
          <div className="grid gap-4">
            <div className="flex flex-col gap-1">
              <p className="text-base font-medium text-slate-800">Vybavení</p>
              <p className="text-xs text-slate-400">{FILTER_HINT}</p>
            </div>
            <div className="-mr-2 max-h-[55vh] overflow-y-auto pr-2">
              <FacilityChecklist selectedKeys={selectedKeys} onToggle={toggle} />
            </div>
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                size="sm"
                className={cn(selectedKeys.length === 0 && 'invisible')}
                onClick={clear}
              >
                Vymazat
              </Button>
              <Button size="sm" onClick={handleClose}>
                Potvrdit
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}

interface FacilityFilterMobileProps {
  onOpenFacilitySelection: () => void
}

export function FacilityFilterMobile({ onOpenFacilitySelection }: FacilityFilterMobileProps) {
  const { localFilters } = useSearchFilters()
  const selectedKeys = parseFacilityKeys(localFilters.facility)

  return (
    <div className="flex items-center justify-between">
      <span className="text-sm font-medium text-slate-800">Vybavení</span>
      <Button
        variant="ghost"
        className="flex h-auto items-center gap-1 font-normal text-slate-600"
        onClick={onOpenFacilitySelection}
      >
        {formatFacilitySummary(selectedKeys)}
        <ChevronRightIcon className="mt-1" />
      </Button>
    </div>
  )
}

interface FacilitySelectionMobileProps {
  onDone: () => void
}

export function FacilitySelectionMobile({ onDone }: FacilitySelectionMobileProps) {
  const { selectedKeys, toggle, clear, trackIfChanged } = useFacilitySelection()

  const handleDone = () => {
    trackIfChanged()
    onDone()
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-xs text-slate-400">{FILTER_HINT}</p>
      <div className="-mr-2 max-h-[55vh] overflow-y-auto pr-2">
        <FacilityChecklist selectedKeys={selectedKeys} onToggle={toggle} />
      </div>
      <div className="flex items-center gap-2">
        {selectedKeys.length > 0 && (
          <Button variant="ghost" className="shrink-0" onClick={clear}>
            Vymazat
          </Button>
        )}
        <Button className="flex-1 items-center justify-center rounded-xl py-6" onClick={handleDone}>
          {selectedKeys.length > 0 ? `Hotovo (${selectedKeys.length})` : 'Hotovo'}
        </Button>
      </div>
    </div>
  )
}
